import { IRestaurantArea, SectionLabel, useConfirmContext } from '@localina/core';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAreas, useUpdateAreaOrderingMutation } from '../../../api/queries/restaurantAreas';
import { useRestaurant } from '../../../api/queries/restaurants';
import { useSortableItemsWrapperUtils } from '../../../utils/SortableItemsUtils';
import { AreaListItem } from './AreaItem';

interface IProps {
    editRestaurantArea: (restaurantArea: IRestaurantArea, virtualAreaId: string) => void;
}

const Areas: React.FC<IProps> = (props) => {
    const { t } = useTranslation();
    const restaurantQuery = useRestaurant();
    const areaListQuery = useAreas();
    const updateAreaOrderingMutation = useUpdateAreaOrderingMutation();

    const { snackbar } = useConfirmContext();

    const [areaList, setAreaList] = useState(areaListQuery.data?.areas || []);

    const { moveItem, findItemIndex } = useSortableItemsWrapperUtils<
        IRestaurantArea & {
            virtualAreaId: string;
        }
    >(areaList, setAreaList);

    useEffect(() => {
        if (areaListQuery.data) {
            setAreaList(areaListQuery.data.areas);
        }
    }, [areaListQuery.data]);

    useEffect(() => {
        if (
            restaurantQuery.data &&
            areaListQuery.data &&
            !restaurantQuery.data.configuration.orderings.areas &&
            updateAreaOrderingMutation.isIdle
        ) {
            updateAreaOrderingMutation.mutate(areaListQuery.data?.areas.map((a) => a.id));
        }
    }, [Boolean(restaurantQuery.data), Boolean(restaurantQuery.data?.configuration.orderings?.areas)]);

    const handleDrop = () => {
        updateAreaOrderingMutation.mutate(
            areaList.map(({ id }) => id),
            {
                onError: () => {
                    snackbar({ severity: 'error', msg: t('sectors.updateAreasOrder.error') });
                    if (areaListQuery.data) {
                        setAreaList(areaListQuery.data.areas);
                    }
                },
            },
        );
    };

    const capacitySlices =
        areaList.length > 1
            ? areaList.map(({ id, name, capacity }) => ({ id, name, capacity: capacity || 0 }))
            : undefined;
    const virtualAreaNames = new Map(
        (restaurantQuery.data?.configuration.virtualAreas ?? []).map((va) => [va.id, va.name]),
    );

    return (
        <div className="restaurant-sectors-areas">
            <SectionLabel value={t('sectors.areas')} />
            <div className="restaurant-sectors-areas__list">
                {areaList.map((area, index) => (
                    <AreaListItem
                        key={area.id}
                        area={area}
                        onClick={props.editRestaurantArea}
                        virtualAreaId={area.virtualAreaId}
                        virtualAreaName={virtualAreaNames.get(area.virtualAreaId)}
                        capacitySlices={capacitySlices}
                        moveItem={moveItem}
                        findItemIndex={findItemIndex}
                        index={index}
                        onDrop={handleDrop}
                    />
                ))}
            </div>
        </div>
    );
};

export default Areas;
